/**
 * LowCreditsModal — iter v33 r30
 * ────────────────────────────────────────────────────────────────────────
 * User: «Alert Crediti Bassi: quando premi Migliora tutte e i crediti non
 * bastano ti avviso in anticipo con opzione "Compra 20 top-up" senza
 * uscire dal flusso».
 *
 * Modal che appare PRIMA di lanciare il batch quando `balance < required`.
 *   - Mostra saldo attuale, quanti servono, foto affrontabili con quelli
 *     che hai.
 *   - Bottone primario: "Compra 20 crediti · €5" → Stripe checkout.
 *   - Bottone secondario: "Vai avanti con {balance} foto" → procede con
 *     quello che si puo' e chi resta va nel badge "Non riuscita".
 *   - Terzo bottone: "Annulla".
 */
import React, { useState } from "react";
import { X, Coins, ArrowRight, ShoppingCartSimple, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Props:
 *   open           boolean
 *   balance        number   — crediti disponibili
 *   required       number   — crediti necessari (photos × cost)
 *   costPerPhoto   number   — di solito 1
 *   photoCount     number   — foto totali da migliorare
 *   onClose()               — chiude senza fare nulla
 *   onProceedPartial()      — procede migliorando `balance/costPerPhoto` foto
 */
export default function LowCreditsModal({
  open,
  balance,
  required,
  costPerPhoto = 1,
  photoCount,
  onClose,
  onProceedPartial,
}) {
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  if (!open) return null;

  const affordable = Math.max(0, Math.floor(balance / Math.max(1, costPerPhoto)));
  const missing = Math.max(0, required - balance);

  const buyTopup = async (sku = "topup_20") => {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/credits/topup/checkout", {
        sku,
        origin_url: window.location.origin,
      });
      const url = data?.url || data?.checkout_url;
      if (url) {
        // Redirect a Stripe checkout — al ritorno il webhook accredita
        window.location.href = url;
      } else {
        toast.error("Non riesco ad aprire il pagamento. Riprova.");
        setCheckoutLoading(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore acquisto crediti";
      toast.error(msg);
      setCheckoutLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1C1C1A]/60 backdrop-blur-sm"
      role="dialog"
      aria-labelledby="low-credits-title"
      data-testid="low-credits-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full shadow-[0_24px_60px_-12px_rgba(28,28,26,0.5)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 border-b border-[#EAE7DF]">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-[#F1F4ED] transition-colors"
            aria-label="Chiudi"
            data-testid="low-credits-close"
          >
            <X size={16} weight="bold" className="text-[#6B6A65]" />
          </button>
          <div className="flex items-center gap-3 mb-1">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#FBF3E0]">
              <Coins size={20} weight="fill" className="text-[#C9B27A]" />
            </span>
            <h3
              id="low-credits-title"
              className="text-[18px] font-serif text-[#1C1C1A]"
            >
              Ti mancano {missing} {missing === 1 ? "credito" : "crediti"}
            </h3>
          </div>
          <p className="text-[13px] text-[#6B6A65] leading-snug pl-13 ml-13">
            Vuoi migliorare <strong className="text-[#1C1C1A]">{photoCount}</strong> foto
            ma hai solo <strong className="text-[#1C1C1A]">{balance}</strong> credit{balance === 1 ? "o" : "i"}.
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Info card: cosa spende ogni foto */}
          <div className="bg-[#F1F4ED] rounded-lg px-4 py-3 text-[12.5px] text-[#3F3D38] leading-relaxed">
            <div className="flex items-center justify-between mb-1">
              <span>Ogni foto migliorata costa</span>
              <strong className="text-[#7C8B6E] tabular-nums">
                {costPerPhoto} cr
              </strong>
            </div>
            <div className="flex items-center justify-between">
              <span>Totale per {photoCount} foto</span>
              <strong className="text-[#1C1C1A] tabular-nums">
                {required} cr
              </strong>
            </div>
          </div>

          {/* Bottone principale: acquista */}
          <button
            onClick={() => buyTopup("topup_20")}
            disabled={checkoutLoading}
            className="w-full inline-flex items-center justify-between gap-3 bg-[#1C1C1A] hover:bg-black disabled:opacity-60 text-white font-semibold py-3.5 px-5 rounded-xl transition-colors"
            data-testid="low-credits-buy-topup"
          >
            <span className="inline-flex items-center gap-2">
              <ShoppingCartSimple size={16} weight="fill" />
              {checkoutLoading ? "Apro il pagamento…" : "Compra 20 crediti · €5"}
            </span>
            <ArrowRight size={14} weight="bold" />
          </button>
          <div className="text-[11px] text-[#9A968B] text-center -mt-1">
            Ti bastano per 20 foto migliorate · non scadono finche&apos; il piano e&apos; attivo
          </div>

          {/* Bottone secondario: procedi con quel che c'e' */}
          {affordable > 0 && affordable < photoCount && (
            <button
              onClick={() => {
                onProceedPartial(affordable);
                onClose();
              }}
              className="w-full inline-flex items-center justify-center gap-2 bg-white border border-[#7C8B6E]/50 hover:bg-[#F1F4ED] text-[#5B6B4E] font-medium py-2.5 px-5 rounded-xl text-[13px] transition-colors"
              data-testid="low-credits-proceed-partial"
            >
              <Sparkle size={13} weight="fill" />
              Migliora {affordable} fot{affordable === 1 ? "o" : "o"} ora, le altre dopo
            </button>
          )}

          {/* Terzo bottone: annulla */}
          <button
            onClick={onClose}
            className="w-full text-[12.5px] text-[#6B6A65] hover:text-[#1C1C1A] py-1.5 transition-colors"
            data-testid="low-credits-cancel"
          >
            Annulla · non miglioro nulla adesso
          </button>
        </div>
      </div>
    </div>
  );
}
